<template>
    <div
        data-testid="note-mention-editor"
        class="relative min-h-[84px] max-h-[350px] overflow-y-auto"
        :class="{ 'cursor-not-allowed bg-gray-100 text-gray-400': disabled || ariaDisabled }"
        @click.self="focus"
        @keydown="onKeydown"
    >
        <span
            data-testid="note-editor-placeholder"
            class="pointer-events-none absolute left-3 top-3 text-gray-500"
            v-if="isEmpty"
        >
            {{ placeholder }}
        </span>
        <editor-content :editor="editor" />
    </div>
</template>

<script setup lang="ts">
    import { onBeforeUnmount, ref, watch } from 'vue';
    import { Editor, EditorContent } from '@tiptap/vue-3';
    import StarterKit from '@tiptap/starter-kit';
    import type { MentionableUser } from '@/hooks/mention/mentionableUsers.ts';
    import { NoteMention } from '@/components/container/lead/notes/mention/NoteMention.ts';
    import {
        parseNoteContent,
        serializeNoteContent,
    } from '@/components/container/lead/notes/mention/noteMentionCodec.ts';
    import { buildNoteMentionSuggestion } from '@/components/container/lead/notes/mention/noteMentionSuggestion.ts';

    defineOptions({ compatConfig: { COMPONENT_V_MODEL: false } });

    Object.assign(EditorContent, {
        compatConfig: {
            MODE: 3,
            RENDER_FUNCTION: false,
        },
    });

    const props = withDefaults(
        defineProps<{
            modelValue?: string;
            users: ReadonlyArray<MentionableUser>;
            placeholder?: string;
            disabled?: boolean;
            ariaDisabled?: boolean;
            ariaLabel?: string;
            ariaDescribedby?: string;
        }>(),
        {
            modelValue: '',
            placeholder: '',
            disabled: false,
            ariaDisabled: false,
            ariaLabel: '',
            ariaDescribedby: '',
        }
    );

    const emit = defineEmits<{
        'update:modelValue': [value: string];
        'mention-selected': [userId: number | string];
        submit: [];
    }>();

    const isApplyingModelValue = ref(false);
    const isEmpty = ref(props.modelValue === '');

    const editorAttributes = () => ({
        class: 'min-h-[84px] max-h-[350px] whitespace-pre-wrap p-3 outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
        'data-testid': 'note-editor-content',
        'aria-label': props.ariaLabel,
        'aria-describedby': props.ariaDescribedby,
        'aria-disabled': String(props.disabled || props.ariaDisabled),
        'aria-multiline': 'true',
        role: 'textbox',
        ...(props.ariaDisabled ? { tabindex: '0' } : {}),
    });

    const editor = new Editor({
        editable: !props.disabled && !props.ariaDisabled,
        content: parseNoteContent(props.modelValue),
        extensions: [
            StarterKit.configure({
                blockquote: false,
                bold: false,
                bulletList: false,
                code: false,
                codeBlock: false,
                dropcursor: false,
                gapcursor: false,
                heading: false,
                horizontalRule: false,
                italic: false,
                listItem: false,
                orderedList: false,
                strike: false,
            }),
            NoteMention.configure({
                suggestion: buildNoteMentionSuggestion(
                    () => props.users,
                    (userId) => emit('mention-selected', userId)
                ),
            }),
        ],
        editorProps: {
            attributes: editorAttributes(),
            handlePaste: (view, event) => {
                const text = event.clipboardData?.getData('text/plain');

                if (text === undefined) {
                    return false;
                }

                event.preventDefault();
                view.dispatch(view.state.tr.insertText(text));
                return true;
            },
        },
        onUpdate: ({ editor: updatedEditor }) => {
            isEmpty.value = updatedEditor.isEmpty;

            if (!isApplyingModelValue.value) {
                emit('update:modelValue', serializeNoteContent(updatedEditor.getJSON()));
            }
        },
    });

    const focus = () => {
        if (props.disabled) {
            return;
        }

        if (props.ariaDisabled) {
            editor.view.dom.focus({ preventScroll: true });
            return;
        }

        editor.commands.focus('end', { scrollIntoView: false });
    };

    const onKeydown = (event: KeyboardEvent) => {
        if (props.disabled || props.ariaDisabled) {
            return;
        }

        if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            emit('submit');
        }
    };

    watch(
        () => props.modelValue,
        (modelValue) => {
            if (serializeNoteContent(editor.getJSON()) === modelValue) {
                return;
            }

            isApplyingModelValue.value = true;
            editor.commands.setContent(parseNoteContent(modelValue));
            isApplyingModelValue.value = false;
            isEmpty.value = editor.isEmpty;
        }
    );

    watch(
        () => [props.disabled, props.ariaDisabled, props.ariaLabel, props.ariaDescribedby] as const,
        ([disabled, ariaDisabled]) => {
            editor.setEditable(!disabled && !ariaDisabled);
            editor.setOptions({
                editorProps: {
                    ...editor.options.editorProps,
                    attributes: editorAttributes(),
                },
            });
        }
    );

    onBeforeUnmount(() => editor.destroy());

    defineExpose({ editor, focus });
</script>

<style scoped>
    :deep(.ProseMirror p) {
        margin: 0;
    }

    :deep(.note-mention) {
        color: #0284c7;
        font-weight: 500;
        white-space: nowrap;
    }

    :global(.tippy-box[data-theme~='note-mention']) {
        background: transparent;
        color: inherit;
    }

    :global(.tippy-box[data-theme~='note-mention'] > .tippy-content) {
        padding: 0;
    }
</style>
